import React, { useState, useEffect, useRef } from "react";
import { Container, Row } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Particle from "../Particle";
import resume from "../../Assets/wayne-cv.docx";
import { AiOutlineDownload } from "react-icons/ai";
import { downloadResumePdf } from "./resumePdf";

function ResumeNew() {
  const previewRef = useRef(null);
  const viewportRef = useRef(null);
  const downloadingRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [previewSize, setPreviewSize] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setLoadError(false);

    async function loadResume() {
      try {
        const [response, { renderAsync }] = await Promise.all([
          fetch(resume, { signal: controller.signal }),
          import("docx-preview"),
        ]);
        if (!response.ok) throw new Error("Unable to load resume");
        const data = await response.arrayBuffer();
        // Render away from React's DOM so an unmounted request cannot modify it.
        const container = document.createElement("div");
        await renderAsync(data, container, container, {
          className: "resume-docx",
          inWrapper: false,
          ignoreLastRenderedPageBreak: false,
          useBase64URL: true,
        });
        // Word's Symbol bullet uses a private glyph absent from many browsers.
        container.querySelectorAll("style").forEach((style) => {
          style.textContent = style.textContent.replace(/\uf0b7/g, "•");
        });
        if (!active) return;
        previewRef.current.replaceChildren(...container.childNodes);
        if (document.fonts) await document.fonts.ready;
        if (active) setLoading(false);
      } catch (error) {
        if (active) {
          setLoadError(true);
          setLoading(false);
        }
      }
    }

    loadResume();
    return () => {
      active = false;
      controller.abort();
    };
  }, [attempt]);

  useEffect(() => {
    if (loading || loadError) return;
    const preview = previewRef.current;
    const viewport = viewportRef.current;

    function resizePreview() {
      const width = preview.scrollWidth;
      if (!width || !viewport.clientWidth) return;
      const style = window.getComputedStyle(viewport);
      const availableWidth = viewport.clientWidth
        - parseFloat(style.paddingLeft || 0) - parseFloat(style.paddingRight || 0);
      const scale = Math.min(1, availableWidth / width);
      setPreviewSize({
        width: width * scale,
        height: preview.scrollHeight * scale,
        scale,
      });
    }

    resizePreview();
    const observer = new ResizeObserver(resizePreview);
    observer.observe(viewport);
    observer.observe(preview);
    return () => observer.disconnect();
  }, [loading, loadError]);

  async function handleDownload() {
    if (loading || loadError || downloadingRef.current) return;
    downloadingRef.current = true;
    setDownloading(true);
    setDownloadError(false);
    try {
      await downloadResumePdf(previewRef.current);
    } catch (error) {
      setDownloadError(true);
    } finally {
      downloadingRef.current = false;
      setDownloading(false);
    }
  }

  const downloadButton = (
    <Button
      variant="primary"
      onClick={handleDownload}
      disabled={loading || loadError || downloading}
      style={{ maxWidth: "250px" }}
    >
      <AiOutlineDownload aria-hidden="true" />
      &nbsp;{downloading ? "Generating PDF…" : "Download CV (PDF)"}
    </Button>
  );

  return (
    <div>
      <Container fluid className="resume-section">
        <Particle />
        <Row style={{ justifyContent: "center", position: "relative" }}>
          {downloadButton}
        </Row>

        <Row className="resume">
          {loading && <p role="status">Loading CV…</p>}
          {loadError && (
            <div role="alert">
              <p>Unable to load the CV. Please try again.</p>
              <Button onClick={() => setAttempt((value) => value + 1)}>
                Retry
              </Button>
            </div>
          )}
          <div
            ref={viewportRef}
            className="resume-preview"
            aria-label="CV preview"
            aria-busy={loading}
            style={{ visibility: loading || loadError ? "hidden" : "visible" }}
          >
            <div
              className="resume-preview-frame"
              style={previewSize && {
                width: previewSize.width,
                height: previewSize.height,
              }}
            >
              <div
                ref={previewRef}
                className="resume-document"
                style={{ transform: `scale(${previewSize?.scale || 1})` }}
              />
            </div>
          </div>
        </Row>

        {downloadError && (
          <p role="alert">Unable to generate the PDF. Please try downloading again.</p>
        )}
        <div className="visually-hidden" role="status">
          {downloading ? "Generating PDF. Your download will start shortly." : ""}
        </div>
        <Row style={{ justifyContent: "center", position: "relative" }}>
          {downloadButton}
        </Row>
      </Container>
    </div>
  );
}

export default ResumeNew;
